// Defaults for the /start Customize screen. Picking a business type fills in
// an example reward, a stamp icon and a card colour so the card looks finished
// from the first second; every value stays editable. Customer-facing text
// (rewards) comes from i18n so a Turkish merchant does not ship English to
// their customers.

// 24x24 stroke icons drawn into the stamp circles. Shapes follow Lucide (ISC
// licence, https://lucide.dev).
export const STAMP_ICONS = {
  star: [
    '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  ],
  heart: [
    '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
  ],
  cup: [
    '<path d="M10 2v2"/>',
    '<path d="M14 2v2"/>',
    '<path d="M6 2v2"/>',
    '<path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1"/>',
  ],
  scissors: [
    '<circle cx="6" cy="6" r="3"/>',
    '<circle cx="6" cy="18" r="3"/>',
    '<path d="M20 4 8.12 15.88"/>',
    '<path d="M14.47 14.48 20 20"/>',
    '<path d="M8.12 8.12 12 12"/>',
  ],
  gift: [
    '<rect x="3" y="8" width="18" height="4" rx="1"/>',
    '<path d="M12 8v13"/>',
    '<path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>',
    '<path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>',
  ],
  sparkles: [
    '<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>',
  ],
  utensils: [
    '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>',
    '<path d="M7 2v20"/>',
    '<path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
  ],
  dumbbell: [
    '<path d="M14.4 14.4 9.6 9.6"/>',
    '<path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z"/>',
    '<path d="m21.5 21.5-1.4-1.4"/>',
    '<path d="M3.9 3.9 2.5 2.5"/>',
    '<path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z"/>',
  ],
  bag: [
    '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>',
    '<path d="M3 6h18"/>',
    '<path d="M16 10a4 4 0 0 1-8 0"/>',
  ],
};

export const STAMP_ICON_IDS = Object.keys(STAMP_ICONS);

export function stampIconSvg(iconId, color = "#FFFFFF") {
  const shapes = STAMP_ICONS[iconId] || STAMP_ICONS.star;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 24 24" ` +
    `fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
    `${shapes.join("")}</svg>`
  );
}

// The same shapes as plain data for React to render (no innerHTML: replacing
// an icon's DOM under a finger mid-tap swallows the click).
const SHAPE = /<(\w+)\s+([^>]*?)\/>/g;
const ATTR = /([\w-]+)="([^"]*)"/g;
export function stampIconShapes(iconId) {
  const shapes = STAMP_ICONS[iconId] || STAMP_ICONS.star;
  const out = [];
  for (const markup of shapes) {
    for (const [, tag, attrs] of markup.matchAll(SHAPE)) {
      const props = {};
      for (const [, name, value] of attrs.matchAll(ATTR)) props[name] = value;
      out.push({ tag, props });
    }
  }
  return out;
}

export function stampIconSvgDataUrl(iconId, color) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(stampIconSvg(iconId, color))}`;
}

// The card colours offered as circles. Business types pick their default from
// this list so a default colour always shows as selected.
export const CARD_COLORS = [
  "#18122B",
  "#4522ED",
  "#7A1D5C",
  "#0F4C45",
  "#7C3A12",
  "#1E3A5F",
  "#4A2C1D",
  "#5B2A86",
];

// `cardType` is only a recommendation shown next to the picker: every business
// starts on a stamp card.
export const BUSINESS_TYPES = [
  { id: "barber", icon: "scissors", color: "#18122B", recommendedType: "stamp" },
  { id: "hair", icon: "scissors", color: "#7A1D5C", recommendedType: "stamp" },
  { id: "cafe", icon: "cup", color: "#4A2C1D", recommendedType: "stamp" },
  { id: "bakery", icon: "cup", color: "#7C3A12", recommendedType: "stamp" },
  { id: "restaurant", icon: "utensils", color: "#1E3A5F", recommendedType: "stamp" },
  { id: "beauty", icon: "sparkles", color: "#5B2A86", recommendedType: "stamp" },
  { id: "gym", icon: "dumbbell", color: "#0F4C45", recommendedType: "membership" },
  { id: "shop", icon: "bag", color: "#18122B", recommendedType: "cashback" },
  { id: "other", icon: "star", color: "#4522ED", recommendedType: "stamp" },
];

export const DEFAULT_BUSINESS_TYPE = "other";

export function getBusinessType(id) {
  return BUSINESS_TYPES.find((type) => type.id === id) || getBusinessType(DEFAULT_BUSINESS_TYPE);
}

// The 7 card types the dashboard supports, in the order the picker shows them.
// Values are the backend `type`; labels reuse passes.form.passType.* copy.
export const CARD_TYPES = [
  "stamp",
  "cashback",
  "cashback_money",
  "balance",
  "discount",
  "membership",
  "coupon",
];

export const STAMP_COUNT_MIN = 3;
export const STAMP_COUNT_MAX = 12;
export const DEFAULT_STAMP_COUNT = 8;
export const DEFAULT_DISCOUNT = 10;

// Paper size for the auto-made flyer: Letter where Letter is the norm, A4
// everywhere else.
const LETTER_COUNTRIES = new Set(["US", "CA", "MX", "PH", "CL", "CO", "VE", "GT", "CR", "PA", "DO", "PR", "SV"]);
export function defaultPaperSize(countryCode) {
  return countryCode && LETTER_COUNTRIES.has(String(countryCode).toUpperCase()) ? "letter" : "a4";
}

// Only symbols the dashboard already offers (constants/currencies.js) — the
// value is printed on the card as-is.
const COUNTRY_CURRENCY = {
  US: "$",
  GB: "£",
  AE: "AED",
  CH: "CHF",
  SG: "S$",
  PL: "PLN",
  DK: "DKK",
  AU: "$",
  CA: "$",
  NZ: "$",
};
const EURO_COUNTRIES = new Set([
  "AT", "BE", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV", "LT",
  "LU", "MT", "NL", "PT", "SK", "SI", "ES", "AD", "MC", "SM", "VA", "ME", "XK",
]);
export function defaultCurrency(countryCode) {
  const code = String(countryCode || "").toUpperCase();
  if (COUNTRY_CURRENCY[code]) return COUNTRY_CURRENCY[code];
  if (EURO_COUNTRIES.has(code)) return "€";
  return "€";
}
